import { EventEmitter, Injectable, Output } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { User } from '../users/model/user.model';
import { MenuItem } from '../locations-groups/menu-item.model';

const httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    rejectUnauthorized: false,
};

@Injectable()
export class ApiService {

    @Output() tokenExpired: EventEmitter<any> = new EventEmitter();
    public readonly apiUrl = environment.apiUrl;
    private readonly apiKey = environment.apiKey;

    public readonly apiUrlLive = environment.apiUrlLive;
    public readonly apiUrlTest = environment.apiUrlTest;

    private readonly unsplashUrl = 'https://api.unsplash.com/';
    private readonly unsplashKey = '...';

    constructor(private http: HttpClient) {

    }

    public login(email: string, password: string): Observable<any[]> {
        return this.http
            .post<any>(
                `${this.apiUrl}/auth/sign-in`,
                { email, password },
                httpOptions
            );
    }

    public requestPasswordReset(email: string): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/auth/request-password-reset/` + email,
                httpOptions
            );
    }

    public sendWelcomeMail(email: string): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/auth-secure/send-welcome-mail/` + email,
                httpOptions
            );
    }

    public resetPassword(email: string, token: string, password: string) {
        return this.http
            .post<any>(
                `${this.apiUrl}/auth/reset-password`,
                { email, token, password },
                httpOptions
            )
    }

    public refreshToken() {
        return this.http
            .get<any>(
                `${this.apiUrl}/auth/refresh-token`,
                httpOptions
            ).subscribe(data => {
                console.log(data);
            });
    }

    public getUsers(offset: number = 0, limit: number = 25) {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/users/${offset}/${limit}`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getUserDetails(user: User) {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/user/${user.id}`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getProjects() {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/projects/company`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getBackendRoles() {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/backend-roles`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public saveUser(user: User) {
        if (user.id) {
            return this.http
                .put<any>(
                    `${this.apiUrl}/private/user/${user.id}`,
                    user,
                    httpOptions
                )
                .pipe(
                    catchError(this.handleError)
                )
                .subscribe();
        } else {
            user.contact.email = user.email
            return this.http
                .post<any>(
                    `${this.apiUrl}/private/user`,
                    user,
                    httpOptions
                )
                .pipe(
                    catchError(this.handleError)
                )
                .subscribe();
        }

    }

    public getChatForCustomer(email: string, orderHash: string): Observable<any[]> {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/public/chat/customer`,
                { email, orderHash }, // dieses obj ist im node express der req.body als z.b. req.body.email usw.
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getOrdersForAdmin(company, from, to, excludeIds: any[] = [], page: number = 1, limit: number = 50, sortBy: string = 'createdAt', sortDirection: string = 'DESC', search: string = '', status: string = '', location: string = '', syncMio: string = '', payment: string = '', paymentFailed: string = '') {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                // `${this.apiUrl}/private/orders`,
               // `https://api.dieshop.app:8082/private/orders`,
               `${this.apiUrl}/private/orders`,
                {
                    company, from, to, excludeIds, page, limit, sortBy, sortDirection, search, status, location, syncMio, payment, paymentFailed
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public resyncOrderToMio(orderId: number, orderHash?: string): Observable<any> {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/orders/resync-mio`,
                {
                    orderId,
                    orderHash
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }

    public syncAllOrdersToMio(location: string, from?: string, to?: string): Observable<any> {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/orders/sync-all-mio`,
                {
                    location,
                    from,
                    to
                },
                { headers }
            )
    }

    public deleteOrder(orderId: number): Observable<any> {
        return this.http
            .delete<any>(
                `${this.apiUrl}/private/order/${orderId}`,
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }

    public updateOrder(orderId: number, updateData: any): Observable<any> {
        return this.http
            .put<any>(
                `${this.apiUrl}/private/order/${orderId}`,
                updateData,
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }

    public getOrderById(orderId: number): Observable<any> {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/order/${orderId}`,
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }

    public getContactData(): Observable<any[]> {
        return this.http.get<any[]>(
            `${this.apiUrl}/private/addresses/`
        );
    }

    private handleError = (error: HttpErrorResponse) => {
        if (error.status === 0) {
            // A client-side or network error occurred. Handle it accordingly.
            console.error('An error occurred:', error.error);
        } else if (error.status == 401 || error.status == 403) {
            console.error(`Server returned ${error.status}. Emitting tokenExpired event...`);
            this.tokenExpired.emit(null);
            return
        } else {
            // The backend returned an unsuccessful response code.
            // The response body may contain clues as to what went wrong.

            console.error(
                `Backend returned code ${error.status}, body was: `, error.error);
        }

        // Return an observable with a user-facing error message.
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }

    addTimeLog(projectId, timeLogId, timeLog = null) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-log`,
                {
                    projectId, timeLogId, timeLog
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deleteTimeLog(timeLog) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-log/delete`,
                {
                    timeLog,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editTimeLog(timeLog) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-log/edit`,
                {
                    timeLog,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editLock(item) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/edit`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    moveLock(item) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/move`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }


    deleteLock(item) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/delete`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    addLock(item) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/add`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getTimeLogs(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/time-logs`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLocationControllerPing(controller) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/ping`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLocationController(controller) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    addLocationController(controller: any) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/add`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editLocationController(controller: any) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/edit`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }
    deleteLocationController(controller: any) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring/controller/delete`,
                {
                    controller,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLocationControllers(filter: any = null) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/monitoring`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getOrderList(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    createAccessRight(payload) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/create`,
                payload,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getAvailableAccessRightBoxes(payload) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/available-boxes`,
                payload,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    sendAccessRightMail(payload) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/send-mail`,
                payload,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getOrderListActive(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/aktive-mieten/34hlkj2345lkjfhu76ftz2ui3gf243tbkjlwhg5kjgh2lb23v`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLogs(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/logs`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getRequestLogs(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/request-logs`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    replayRequestLog(id: number) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .get<any>(
                `${this.apiUrl}/private/request-log/${id}/replay`,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getItemList(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/items`,
                {
                    filter,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getTextConstants(filter) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/text-constants`,
                {
                    filter
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    updateTextConstant(item) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/text-constant/edit`,
                {
                    item,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLockLogs(type, nrs: any[]) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/logs/${type}`,
                { code: nrs || [] },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getLockStatistics(locationId: number) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/statistics/` + locationId,
                {},
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editOrderItem(orderItem) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/edit`,
                {
                    orderItem,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    createManualAccessRight(accessRight) {
        const headers = new HttpHeaders({

        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/create`,
                {
                    accessRight,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    editOrderItemDate(orderItem) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/private/access-rights/editDate`,
                {
                    orderItem,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getCompanies() {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/companies`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getDomains(): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/private/domains`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public getTimezones(includeInactive: boolean = false): Observable<any[]> {
        const query = includeInactive ? '?includeInactive=true' : '';

        return this.http
            .get<any>(
                `${this.apiUrl}/private/timezones${query}`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public createTimezone(timezone: any): Observable<any> {
        return this.http
            .post<any>(
                `${this.apiUrl}/private/timezone`,
                { timezone },
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public updateTimezone(timezone: any): Observable<any> {
        return this.http
            .put<any>(
                `${this.apiUrl}/private/timezone/${timezone.id}`,
                { timezone },
                httpOptions
            )
    }


    public getFranchisees(): Observable<any[]> {
        return this.http
            .get<any>(
                `${this.apiUrl}/franchisees/`,
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    public editFranchisee(franchisee) {
        return this.http
            .post<any>(
                `${this.apiUrl}/franchisees/`,
                {
                    franchisee,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }


    addFranchisee(franchisee) {
        return this.http
            .post<any>(
                `${this.apiUrl}/franchisees/add`,
                {
                    franchisee,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public getStorageLocations(options: any = [], locationId: number = 0): Observable<any[]> {
        let ordering = options.orderBy ? ('?orderBy=' + options.orderBy[0] + '&dir=' + options.orderBy[1]) : '';
        let appendLocationId = locationId > 0 ? '&locationId=' + locationId : ''

        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-locations` + ordering + appendLocationId,
                {
                    filter: options
                },
                httpOptions
            )
            .pipe(
                catchError(this.handleError)
            )
    }

    editStorageLocation(storageLocation) {
        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-location/${storageLocation.id}`,
                {
                    storageLocation,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    archivateStorageLocation(location: any) {
        const headers = new HttpHeaders({

        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-location-delete`,
                {
                    location,
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    restoreStorageLocation(locationId: number) {
        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-location/${locationId}/restore`,
                {},
                httpOptions
            )
            .pipe(
                map((res: any) => res),
                catchError(this.handleError)
            );
    }


    addStorageLocation(storageLocation) {
        return this.http
            .post<any>(
                `${this.apiUrl}/location/storage-location`,
                {
                    storageLocation,
                },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    public sendGetCall(url, terminal: any = null): Observable<any[]> {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/send-get-call`,
                {
                    url,
                    terminal
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    updateDomain(domain) {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/domains/edit`,
                {
                    domain
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deleteDomain(domain) {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/private/domains/delete`,
                {
                    domain
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    openLockAsAdmin(item, sendAltSignal: boolean = false, type: string = '') {
        const headers = new HttpHeaders({
            
        });
        return this.http
            .post<any>(
                `${this.apiUrl}/location/lock/open`,
                {
                    item, sendAltSignal, type
                },
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    deletePriceGroupEntry(id) {
        const headers = new HttpHeaders({
            
        });

        return this.http
            .post<any>(
                `${this.apiUrl}/location/price/delete`,
                {
                    id: id,
                },
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            );
    }

    getStatisticsFilter() {
        const headers = new HttpHeaders({
            
        });

        console.log('Calling ' + `${this.apiUrl}/private/statistics-filter`)
        return this.http
            .get<any>(
                `${this.apiUrl}/private/statistics-filter`,
                { headers }
            ).pipe(
                map((res: any) => {
                    console.log(846, res)
                    return res;
                })
            )
    }

    getCouponCodes(filter: any = {}) {
        const headers = new HttpHeaders({});
        return this.http
            .post<any>(
                `${this.apiUrl}/private/coupon-codes`,
                {
                    filter
                },
                { headers }
            ).pipe(
                map((res: any) => {
                    return Array.isArray(res) ? res : [];
                }),
                catchError(this.handleError)
            )
    }

    getControllingStatistics(filter: any) {
        console.log(853, filter)

        if (filter.couponCode === null) {
            delete filter.couponCode
        }

        const headers = new HttpHeaders({
            
        });

        console.log('Calling ' + `${this.apiUrl}/private/statistics`)
        return this.http
            .post<any>(
                `${this.apiUrl}/private/statistics`,
                {
                    filter: filter
                },
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            )
    }

    getCoupons(filter: any = {}) {
        const headers = new HttpHeaders({});
        const params = new URLSearchParams();

        if (filter.search) {
            params.set('search', filter.search);
        }
        if (filter.active) {
            params.set('active', filter.active);
        }
        if (filter.locationId !== undefined && filter.locationId !== null && filter.locationId !== '') {
            params.set('locationId', `${filter.locationId}`);
        }

        const query = params.toString() ? `?${params.toString()}` : '';
        return this.http
            .get<any>(
                `${this.apiUrl}/private/coupons${query}`,
                { headers }
            )
            .pipe(
                map((res: any) => {
                    return res || [];
                }),
                catchError(this.handleError)
            );
    }

    createCoupon(coupon: any) {
        return this.http
            .post<any>(
                `${this.apiUrl}/private/coupon`,
                { coupon },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }

    updateCoupon(couponId: number, coupon: any) {
        return this.http
            .put<any>(
                `${this.apiUrl}/private/coupon/${couponId}`,
                { coupon },
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }

    deleteCoupon(couponId: number) {
        return this.http
            .delete<any>(
                `${this.apiUrl}/private/coupon/${couponId}`,
                httpOptions
            )
            .pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            );
    }


    getLocationGroups(): Observable<MenuItem[]> {
        const headers = new HttpHeaders({});

        console.log('Calling ' + `${this.apiUrl}/private/locationgroups`)
        return this.http
            .get<MenuItem[]>(
                `${this.apiUrl}/private/locationgroups`,
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            )
    }
    
    createLocationGroup(locationGroup: MenuItem): Observable<MenuItem> {
        const headers = new HttpHeaders({});

        console.log('Calling ' + `${this.apiUrl}/private/locationgroup`)
        return this.http
            .post<MenuItem>(
                `${this.apiUrl}/private/locationgroup`,
                locationGroup.toPlainObject(),
                { headers }
            ).pipe(
                map((res: any) => {
                    return new MenuItem(res);
                })
            )
    }

    updateLocationGroup(locationGroup: MenuItem): Observable<MenuItem> {
        const headers = new HttpHeaders({});

        console.log('Calling ' + `${this.apiUrl}/private/locationgroup/${locationGroup.id}`)
        return this.http
            .post<MenuItem>(
                `${this.apiUrl}/private/locationgroup/${locationGroup.id}`,
                locationGroup.toPlainObject(),
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            )
    }

    deleteLocationGroup(locationGroup: MenuItem): Observable<MenuItem> {
        const headers = new HttpHeaders({});

        console.log('Calling ' + `${this.apiUrl}/private/locationgroup/${locationGroup.id}/delete`)
        return this.http
            .post<MenuItem>(
                `${this.apiUrl}/private/locationgroup/${locationGroup.id}/delete`,
                locationGroup.toPlainObject(),
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                })
            )
    }

    importPrices(payload: any): Observable<any> {
        const headers = new HttpHeaders({});

        console.log('Calling ' + `${this.apiUrl}/location/price/import`)
        return this.http
            .post<any>(
                `${this.apiUrl}/location/price/import`,
                payload,
                { headers }
            ).pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            )
    }

    importPricesFromExcel(formData: FormData): Observable<any> {
        console.log('Calling ' + `${this.apiUrl}/location/price/import-excel`)
        return this.http
            .post<any>(
                `${this.apiUrl}/location/price/import-excel`,
                formData
            ).pipe(
                map((res: any) => {
                    return res;
                }),
                catchError(this.handleError)
            )
    }
}
